import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  useTheme,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import AppDropDown from "../../../../../views/components/common/filter/AppDropDown";
import FormTextField from "../../../../../views/components/common/form/FormTextField";
import { PurchaseRequestProduct } from "../../../../../models/PurchaseRequest/PurchaseRequestProductInsights";
import { VGBrand } from "../../../../../models/Vouchers/VGBrand";
import { VGVoucherStock } from "../../../../../models/Vouchers/VGVoucherStock";
import VouchersAPI from "../../../api/VouchersAPI";
import CustomTextField from "../../../../../views/components/common/textFields/TextField";
import { useNotification } from "../../../../../utils/NotificationsContext";

interface PurchaseVouchersPopupProps {
  open: boolean;
  onClose: () => void;
  product: PurchaseRequestProduct;
  pr_id: number;
}

const PurchaseVouchersPopup: React.FC<PurchaseVouchersPopupProps> = ({
  open,
  onClose,
  product,
  pr_id,
}) => {
  const theme = useTheme();
  const { showSuccess } = useNotification();
  const vouchersAPI = new VouchersAPI();
  const [quantity, setQuantity] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [purchasing, setPurchasing] = useState<boolean>(false);
  const [brandData, setBrandData] = useState<VGBrand | null>(null);
  const [denomination, setDenomination] = useState<string>("0");
  const [denominationError, setDenominationError] = useState<string>("");
  const [checkingStock, setCheckingStock] = useState<boolean>(false);
  const [stockData, setStockData] = useState<VGVoucherStock | null>(null);
  const [canPurchase, setCanPurchase] = useState<boolean>(false);
  const [apiError, setApiError] = useState<string | null>(null);

  // Initialize quantity from product when popup opens
  useEffect(() => {
    if (open && product) {
      setQuantity((product.quantity - product.voucher_count).toString() || "0");
      setDenomination("");
      setDenominationError("");
      setBrandData(null);
      setStockData(null);
      setCanPurchase(false);
      setApiError(null);

      // API call on popup launch - placeholder
      fetchBrandData();
    }
  }, [open, product]);

  // Update canPurchase when stock data is available
  useEffect(() => {
    if (stockData && denomination) {
      const availableQty = parseInt(stockData.AvailableQuantity || "0");
      const prQty = parseInt(quantity);
      setCanPurchase(availableQty > prQty);
    } else {
      setCanPurchase(false);
    }
  }, [stockData, denomination, quantity]);

  // Fetch brand data - placeholder API call
  const fetchBrandData = async () => {
    setLoading(true);
    setApiError(null);
    try {
      const response = await vouchersAPI.getBrandDetailsByProductId(
        product.product_id
      );
      setBrandData(response);

      setDenomination("0");
    } catch (error: any) {
      console.error("Error fetching brand data:", error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to fetch brand data";
      setApiError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Check stock availability - placeholder API call
  const checkStockAvailability = async (selectedDenomination: string) => {
    if (!selectedDenomination || !brandData) return;

    if (selectedDenomination === "0") {
      setStockData(null);
      return;
    }

    setCheckingStock(true);
    setDenominationError("");
    setApiError(null);
    try {
      const response =
        await vouchersAPI.getVoucherStockByProductIdAndDenomination(
          brandData.BrandProductCode,
          selectedDenomination
        );
      setStockData(response);
    } catch (error: any) {
      console.error("Error checking stock:", error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to check stock availability";
      setApiError(errorMessage);
      setDenominationError("Failed to check stock availability");
      setStockData(null);
      setCanPurchase(false);
    } finally {
      setCheckingStock(false);
    }
  };

  // Handle denomination change
  const handleDenominationChange = (value: string) => {
    setDenomination(value);
    setDenominationError("");
    setApiError(null);
    setStockData(null);
    setCanPurchase(false);

    if (value) {
      if (!brandData) {
        setDenominationError("Brand data not available");
        return;
      }

      // Validate denomination
      if (brandData.denominationList) {
        // If it's a dropdown, value is already valid
        checkStockAvailability(value);
      } else {
        // If it's a text field, validate min/max
        const numValue = parseFloat(value);
        if (
          brandData.MinValue !== null &&
          brandData.MaxValue !== null &&
          (numValue < brandData.MinValue || numValue > brandData.MaxValue)
        ) {
          setDenominationError(
            `Denomination must be between ${brandData.MinValue} and ${brandData.MaxValue}`
          );
          return;
        }
        checkStockAvailability(value);
      }
    }
  };

  // Handle purchase - placeholder API call
  const handlePurchase = async () => {
    if (!brandData || !denomination || !canPurchase) return;

    setApiError(null);
    setPurchasing(true);
    try {
      await vouchersAPI.purchaseVoucher(
        product.product_id,
        brandData.BrandProductCode,
        pr_id,
        parseInt(quantity),
        denomination
      );

      showSuccess(
        "Voucher purchase request placed successfully! Refresh the page to see the updated quantity."
      );
      onClose();
    } catch (error: any) {
      console.error("Error purchasing voucher:", error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to purchase voucher";
      setApiError(errorMessage);
    } finally {
      setPurchasing(false);
    }
  };

  // Parse denomination list
  const getDenominationOptions = (): { id: string; value: string }[] => {
    if (!brandData?.denominationList || brandData.denominationList === "")
      return [{ id: "0", value: "Select" }];
    return [{ id: "0", value: "Select" }].concat(
      brandData.denominationList.split(",").map((denom) => ({
        id: denom.trim(),
        value: denom.trim(),
      }))
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      slotProps={{
        transition: {
          timeout: 300,
          sx: {
            backgroundColor:
              theme.palette.customColors?.backdrop || "rgba(0, 0, 0, 0.4)",
            backdropFilter: "blur(4px)",
          },
        },
        backdrop: {
          timeout: 300,
          sx: {
            backgroundColor:
              theme.palette.customColors?.backdrop || "rgba(0, 0, 0, 0.4)",
            backdropFilter: "blur(4px)",
          },
        },
        paper: {
          sx: {
            borderRadius: "16px",
            boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15)",
            overflow: "hidden",
            maxHeight: "90vh",
            maxWidth: {
              xs: "95vw",
              sm: "90vw",
              md: "600px",
            },
            width: {
              xs: "95vw",
              sm: "90vw",
              md: "600px",
            },
            margin: { xs: "16px", sm: "32px" },
            background: `linear-gradient(135deg, ${
              theme.palette.customColors?.white ||
              theme.palette.background.paper
            } 0%, ${
              theme.palette.customColors?.lightGreyColor ||
              theme.palette.grey[50]
            } 100%)`,
          },
        },
      }}
      sx={{
        zIndex: 2000,
        "& .MuiDialog-container": {
          alignItems: "center",
          justifyContent: "center",
        },
        "& .MuiDialog-paper": {
          zIndex: 2000,
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          position: "relative",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "4px",
            background: `linear-gradient(90deg, ${
              theme.palette.customColors?.blueGradientStart ||
              theme.palette.primary.main
            } 0%, ${
              theme.palette.customColors?.blueGradientEnd ||
              theme.palette.primary.dark
            } 50%, ${
              theme.palette.customColors?.blueGradientStart ||
              theme.palette.primary.main
            } 100%)`,
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: { xs: "12px 16px 12px", sm: "16px 20px 16px" },
            borderBottom: `1px solid ${
              theme.palette.customColors?.greyBorder || theme.palette.divider
            }`,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            <CustomTypography
              variant="heading4"
              fontWeight={600}
              color={theme.palette.customColors?.blackBorder || "text.primary"}
            >
              Purchase Vouchers
            </CustomTypography>
          </Box>
          <IconButton
            onClick={onClose}
            sx={{
              color:
                theme.palette.customColors?.greyTextColor ||
                theme.palette.text.secondary,
              "&:hover": {
                backgroundColor:
                  theme.palette.customColors?.lightGreyColor ||
                  theme.palette.grey[100],
                color:
                  theme.palette.customColors?.blackBorder ||
                  theme.palette.text.primary,
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Content */}
        <DialogContent
          sx={{
            padding: { xs: "20px", sm: "24px" },
            flex: 1,
            overflow: "auto",
            maxHeight: "60vh",
            "&::-webkit-scrollbar": {
              width: "8px",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor:
                theme.palette.customColors?.greyBackground ||
                theme.palette.grey[100],
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor:
                theme.palette.customColors?.greyDarkborder ||
                theme.palette.grey[400],
              borderRadius: "4px",
              "&:hover": {
                backgroundColor:
                  theme.palette.customColors?.greyTextColor ||
                  theme.palette.grey[600],
              },
            },
          }}
        >
          {loading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "200px",
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {/* Product Name */}
              <Box>
                <CustomTypography
                  variant="body2"
                  color="text.secondary"
                  fontWeight={500}
                  style={{ marginBottom: 4 }}
                >
                  Product Name
                </CustomTypography>
                <CustomTypography
                  variant="body1"
                  fontWeight={600}
                  color="text.primary"
                >
                  {product.product_name}
                </CustomTypography>
              </Box>

              {/* Quantity */}
              <Box>
                <CustomTypography
                  variant="body2"
                  color="text.secondary"
                  fontWeight={500}
                  style={{ marginBottom: 8 }}
                >
                  Quantity
                </CustomTypography>
                <CustomTextField
                  type="number"
                  placeholder="Enter quantity"
                  value={quantity}
                  width="50%"
                  onChange={(e) => {
                    setQuantity(e.target.value || "");
                    setApiError(null);
                  }}
                />
              </Box>

              {/* Denomination */}
              {brandData && (
                <Box>
                  <CustomTypography
                    variant="body2"
                    color="text.secondary"
                    fontWeight={500}
                    style={{ marginBottom: 8 }}
                  >
                    Denomination
                    {brandData.MinValue !== null &&
                      brandData.MaxValue !== null && (
                        <CustomTypography
                          variant="caption"
                          color="text.secondary"
                          style={{ marginLeft: 4 }}
                        >
                          (Min: {brandData.MinValue}, Max: {brandData.MaxValue})
                        </CustomTypography>
                      )}
                  </CustomTypography>

                  {brandData.denominationList ? (
                    <Box sx={{ width: "50%" }}>
                      <AppDropDown
                        isMultiSelect={false}
                        title=""
                        height="48px"
                        listHeight="40px"
                        fontSize="0.8rem"
                        value={denomination}
                        onSingleSelectionChange={(id, name) =>
                          handleDenominationChange(id)
                        }
                        list={getDenominationOptions()}
                        id="denomination"
                        menuZIndex={3000}
                        placeholderText="Select denomination"
                      />
                    </Box>
                  ) : (
                    // Text field for custom denomination
                    <CustomTextField
                      type="number"
                      value={denomination}
                      onChange={(e) => {
                        handleDenominationChange(e.target.value || "");
                      }}
                      placeholder={`Enter between ${
                        brandData.MinValue || 0
                      } and ${brandData.MaxValue || 0}`}
                    />
                  )}

                  {checkingStock && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mt: 1,
                      }}
                    >
                      <CircularProgress size={16} />
                      <CustomTypography
                        variant="caption"
                        color="text.secondary"
                      >
                        Checking stock availability...
                      </CustomTypography>
                    </Box>
                  )}

                  {stockData && (
                    <Box
                      sx={{
                        mt: 2,
                        p: 2,
                        backgroundColor: theme.palette.info.main + "10",
                        borderRadius: 2,
                        border: `1px solid ${theme.palette.info.main + "30"}`,
                      }}
                    >
                      <CustomTypography
                        variant="body2"
                        color="info.main"
                        fontWeight={500}
                      >
                        Available Quantity: {stockData.AvailableQuantity}
                      </CustomTypography>
                    </Box>
                  )}
                </Box>
              )}

              {/* API Error Display */}
              {apiError && (
                <Box
                  sx={{
                    mt: 2,
                    p: 2,
                    backgroundColor: "#fff",
                    borderRadius: 2,
                    border: "1px solid #DC3545",
                  }}
                >
                  <CustomTypography
                    variant="body2"
                    sx={{
                      color: "#DC3545",
                      fontWeight: 500,
                    }}
                  >
                    {apiError}
                  </CustomTypography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>

        {/* Footer Actions */}
        <DialogActions
          sx={{
            padding: { xs: "16px 20px 20px", sm: "20px 24px 24px" },
            borderTop: `1px solid ${
              theme.palette.customColors?.greyBorder || theme.palette.divider
            }`,
            backgroundColor:
              theme.palette.customColors?.lightGreyColor ||
              theme.palette.grey[50],
            gap: 2,
            justifyContent: "flex-end",
          }}
        >
          <AppButton
            onClick={onClose}
            variant="cancel"
            customStyles={{ minWidth: 140, height: 40 }}
            label="Cancel"
          />

          {canPurchase && (
            <AppButton
              onClick={handlePurchase}
              variant="done"
              customStyles={{ minWidth: 140, height: 40 }}
              label="Purchase Voucher"
              disabled={purchasing}
              startIcon={
                purchasing ? (
                  <CircularProgress size={16} color="inherit" />
                ) : undefined
              }
            />
          )}
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default PurchaseVouchersPopup;
